import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, hapusTerkonfirmasi, type Camera } from '../lib/api';
import { DialogHapus } from '../components/DialogHapus';
import { useBahasa } from '../i18n';

/**
 * Custom Monitors (§F9) — susunan kamera rakitan sendiri.
 *
 * Bedanya dari Dashboard, dan alasan ia halaman tersendiri: dashboard itu
 * **pemberian** — admin yang menyusunnya, dan satu dashboard dipakai banyak
 * orang. Monitor itu **rakitan** — milik satu orang, dinamai sesukanya, tidak
 * pernah dibagikan. Orang yang dapat satu dashboard berisi dua puluh kamera
 * sering cuma mengurus empat di antaranya; sampai sekarang tidak ada cara
 * menyimpan keempat itu sebagai satu layar.
 *
 * Yang bisa dipilih hanya kamera yang **sudah** boleh dilihat orang ini — daftar
 * di bawah datang dari `GET /cameras`, yang sudah difilter per assignment (§C2),
 * dan server menyaringnya sekali lagi saat menyimpan (§F9). Merakit monitor
 * tidak pernah menambah akses, ia menyusun ulang akses yang ada.
 */

interface Monitor {
  id: string;
  name: string;
  updatedAt: string;
  cameraIds: string[];
}

interface Draft {
  id: string | null;
  name: string;
  cameraIds: string[];
}

const KOSONG: Draft = { id: null, name: '', cameraIds: [] };

export function MonitorsPage() {
  const { t } = useBahasa();
  const [monitors, setMonitors] = useState<Monitor[] | null>(null);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [cari, setCari] = useState('');
  /** Dashboard yang sedang dikonfirmasi penghapusannya (§G17). */
  const [akanDihapus, setAkanDihapus] = useState<Monitor | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([
        api<{ monitors: Monitor[] }>('/monitors'),
        api<{ cameras: Camera[] }>('/cameras'),
      ]);
      setMonitors(m.monitors);
      setCameras(c.cameras);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const namaKamera = useMemo(
    () => new Map(cameras.map((c) => [c.id, c.name])),
    [cameras],
  );

  const terlihat = useMemo(() => {
    const q = cari.trim().toLowerCase();
    return q ? cameras.filter((c) => c.name.toLowerCase().includes(q)) : cameras;
  }, [cameras, cari]);

  async function simpan() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await api(draft.id ? `/monitors/${draft.id}` : '/monitors', {
        method: draft.id ? 'PUT' : 'POST',
        body: JSON.stringify({ name: draft.name.trim(), cameraIds: draft.cameraIds }),
      });
      setDraft(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Sampai 7 September 2026 fungsi ini dipanggil **langsung dari tombolnya**:
   * tidak ada konfirmasi apa pun, dan tombolnya duduk tepat di sebelah Edit.
   * Sekarang ia hanya bisa dicapai lewat `DialogHapus` (§G17).
   *
   * Galatnya sengaja dilempar, bukan ditulis ke banner halaman: banner itu ada
   * di belakang overlay dialog, dan "password salah" yang mendarat di sana
   * tidak terlihat sama sekali oleh orang yang sedang menatap dialognya.
   */
  async function hapus(m: Monitor, konfirmasi: Parameters<typeof hapusTerkonfirmasi>[1]) {
    await hapusTerkonfirmasi(`/monitors/${m.id}`, konfirmasi);
    setAkanDihapus(null);
    await load();
  }

  function pilih(id: string) {
    setDraft((d) =>
      d
        ? {
            ...d,
            cameraIds: d.cameraIds.includes(id)
              ? d.cameraIds.filter((x) => x !== id)
              : [...d.cameraIds, id],
          }
        : d,
    );
  }

  if (monitors === null) {
    return (
      <p className="p-8 text-sm text-muted" role="status">
        {t('Loading dashboards…')}
      </p>
    );
  }

  const siap = draft !== null && draft.name.trim() !== '' && draft.cameraIds.length > 0;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-xl">{t('Custom Dashboards')}</h1>
        {draft === null && (
          <button
            type="button"
            onClick={() => setDraft(KOSONG)}
            className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
          >
            {t('New dashboard')}
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-md border border-red-600 px-3 py-2 text-sm">
          {error}
        </p>
      )}

      {draft !== null ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void simpan();
          }}
          className="mt-6 rounded-lg border border-line bg-surface p-4"
        >
          <label className="block text-sm">
            {t('Name')}
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('Front gate at night')}
              maxLength={128}
              autoFocus
              className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm"
            />
          </label>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted">
              {t('{dipilih} of {total} cameras selected', {
                dipilih: draft.cameraIds.length,
                total: cameras.length,
              })}
            </span>
            <input
              type="search"
              value={cari}
              onChange={(e) => setCari(e.target.value)}
              placeholder={t('Search cameras')}
              className="ml-auto min-h-tap rounded-md border border-line bg-surface px-3 text-sm sm:w-56"
            />
          </div>

          {/* Daftar dibatasi tingginya lalu digulung, alasan yang sama dengan
              tabel proyeksi di /admin (§D5.1): pada 96 kamera daftar yang tumbuh
              sepenuhnya mendorong tombol Save jauh di bawah lipatan, dan tombol
              yang tidak terlihat sama saja dengan tombol yang tidak ada. */}
          <div className="mt-2 max-h-[19rem] overflow-y-auto rounded-md border border-line">
            {terlihat.length === 0 ? (
              <p className="p-3 text-sm text-muted">
                {cameras.length === 0
                  ? t('No cameras have been assigned to your account yet.')
                  : t('No camera matches “{cari}”.', { cari: cari.trim() })}
              </p>
            ) : (
              <ul>
                {terlihat.map((c) => (
                  <li key={c.id}>
                    <label className="flex min-h-tap cursor-pointer items-center gap-2.5 border-b border-line px-3 text-sm last:border-b-0 hover:bg-subtle">
                      <input
                        type="checkbox"
                        checked={draft.cameraIds.includes(c.id)}
                        onChange={() => pilih(c.id)}
                      />
                      <span className="min-w-0 flex-1 truncate">{c.name}</span>
                      {!c.recordEnabled && (
                        <span className="text-xs text-muted">{t('View only')}</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setCari('');
              }}
              className="min-h-tap rounded-md border border-line px-4 text-sm hover:bg-subtle"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={!siap || busy}
              className="min-h-tap rounded-md bg-accent px-4 text-sm font-semibold text-accent-contrast disabled:opacity-40"
            >
              {busy ? t('Saving…') : draft.id ? t('Save changes') : t('Create dashboard')}
            </button>
          </div>
        </form>
      ) : monitors.length === 0 ? (
        <p className="mt-8 text-sm text-muted">
          {cameras.length === 0
            ? t(
                'No cameras have been assigned to your account yet, so there is nothing to build a dashboard from.',
              )
            : t('No custom dashboards yet. Build one from the cameras you already have.')}
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {monitors.map((m) => (
            <li
              key={m.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface p-3"
            >
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base">{m.name}</h2>
                <p className="mt-0.5 truncate text-xs text-muted">
                  {t('{n} cameras', { n: m.cameraIds.length })} ·{' '}
                  {m.cameraIds
                    .map((id) => namaKamera.get(id) ?? t('Removed camera'))
                    .slice(0, 4)
                    .join(', ')}
                  {m.cameraIds.length > 4 &&
                    ` ${t('+{n} more', { n: m.cameraIds.length - 4 })}`}
                </p>
              </div>
              {/* Membuka monitor memakai halaman Dashboard yang sudah ada, bukan
                  pemutar baru: grid, slideshow, layar penuh, dan paging sudah
                  hidup di sana, dan menyalinnya ke halaman kedua berarti dua
                  tempat yang harus diperbaiki setiap kali salah satunya berubah. */}
              <Link
                to={`/dashboard?monitor=${m.id}`}
                className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast flex items-center"
              >
                {t('Open')}
              </Link>
              <button
                type="button"
                onClick={() => setDraft({ id: m.id, name: m.name, cameraIds: m.cameraIds })}
                className="min-h-tap rounded-md border border-line px-3 text-sm hover:bg-subtle"
              >
                {t('Edit')}
              </button>
              <button
                type="button"
                onClick={() => setAkanDihapus(m)}
                className="min-h-tap rounded-md border border-red-600 px-3 text-sm text-red-600 hover:bg-red-600/10"
              >
                {t('Delete')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {akanDihapus && (
        <DialogHapus
          nama={akanDihapus.name}
          judul={t('Delete dashboard {nama}', { nama: akanDihapus.name })}
          keterangan={t(
            'The layout is deleted. The cameras in it are not touched — only this arrangement is gone.',
          )}
          onBatal={() => setAkanDihapus(null)}
          onHapus={(k) => hapus(akanDihapus, k)}
        />
      )}
    </div>
  );
}
